import { useState, useEffect, useCallback, useRef } from 'react';

interface Message {
  id: number | string;
  content: string;
  type: 'user' | 'ai';
  timestamp: Date;
  [key: string]: any;
}

interface UseLazyMessagesOptions {
  initialBatchSize?: number;
  batchSize?: number;
  loadDirection?: 'top' | 'bottom';
}

/**
 * Hook for lazy loading messages with pagination support
 * Loads messages in batches to prevent blocking UI with large datasets
 */
export function useLazyMessages(
  allMessages: Message[],
  options: UseLazyMessagesOptions = {}
) {
  const {
    initialBatchSize = 20,
    batchSize = 15,
    loadDirection = 'top'
  } = options;

  const [displayedMessages, setDisplayedMessages] = useState<Message[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadedCountRef = useRef(0);
  const allMessagesRef = useRef<Message[]>([]);

  // Initialize with the latest messages
  useEffect(() => {
    allMessagesRef.current = allMessages;
    
    if (allMessages.length === 0) {
      setDisplayedMessages([]);
      setHasMore(false);
      loadedCountRef.current = 0;
      return;
    }

    // Load initial batch (most recent messages)
    const initial = allMessages.slice(-initialBatchSize);
    setDisplayedMessages(initial);
    loadedCountRef.current = initial.length;
    setHasMore(allMessages.length > initialBatchSize);
  }, [allMessages, initialBatchSize]);

  // Load more older messages
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    // Simulate async loading for smooth UX
    requestAnimationFrame(() => {
      const currentCount = loadedCountRef.current;
      const totalCount = allMessagesRef.current.length;
      
      if (currentCount >= totalCount) {
        setHasMore(false);
        setIsLoadingMore(false);
        return;
      }

      // Calculate how many more messages to load
      const remainingCount = totalCount - currentCount;
      const toLoad = Math.min(batchSize, remainingCount);
      
      // Get the next batch of older messages
      const startIndex = totalCount - currentCount - toLoad;
      const endIndex = totalCount - currentCount;
      const olderMessages = allMessagesRef.current.slice(startIndex, endIndex);

      // Prepend older messages to the displayed list
      setDisplayedMessages(prev => [...olderMessages, ...prev]);
      loadedCountRef.current += toLoad;
      
      // Check if there are more messages to load
      setHasMore(loadedCountRef.current < totalCount);
      setIsLoadingMore(false);
    });
  }, [isLoadingMore, hasMore, batchSize]);

  // Reset when switching chats or clearing messages
  const reset = useCallback(() => {
    setDisplayedMessages([]);
    setHasMore(false);
    setIsLoadingMore(false);
    loadedCountRef.current = 0;
  }, []);

  return {
    displayedMessages,
    hasMore,
    isLoadingMore,
    loadMore,
    reset,
    totalCount: allMessages.length,
    loadedCount: loadedCountRef.current
  };
}
